import React, {
  createContext,
  useEffect,
  useState,
  useContext,
  useMemo,
} from "react";
import {
  onAuthStateChanged,
  type User as FirebaseAuthUser,
  signOut,
} from "firebase/auth";
import { Navigate, Outlet } from "react-router";
import { CircularProgress, Container } from "@mui/material";
import useSWR from "swr";
import { auth } from "./firebase";
import { type AdminUser } from "components/adminUserModel";
import { swrKey, adminUserFetcher, swrOption } from "components/adminUserApi";

interface AuthContextType {
  user: FirebaseAuthUser | null;
  firestoreUser: AdminUser | undefined;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  firestoreUser: undefined,
  loading: true,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<FirebaseAuthUser | null>(null);
  const [isLoadingAuthUser, setIsLoadingAuthUser] = useState<boolean>(true);

  const {
    data: firestoreUser,
    isLoading: isLoadingFirestoreUser,
    mutate: mutateSWR,
  } = useSWR(swrKey, adminUserFetcher, swrOption);

  const loading = useMemo(() => {
    return isLoadingAuthUser || isLoadingFirestoreUser;
  }, [isLoadingAuthUser, isLoadingFirestoreUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setIsLoadingAuthUser(false);
      mutateSWR(undefined, { revalidate: true });
      if (import.meta.env.DEV) {
        console.log("Auth state changed:", { currentUser });
      }
    });
    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, firestoreUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

export const ProtectedRoute = () => {
  const { user, firestoreUser, loading } = useAuth();

  if (loading)
    return (
      <Container sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Container>
    );

  if (!user || !firestoreUser) {
    if (user) signOut(auth);
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

export function useAuthenticatedUser() {
  const { user, loading } = useAuth();

  if (loading) {
    throw new Error("Authentication is still loading");
  }

  if (!user) {
    throw new Error("User is not authenticated");
  }

  // ここに到達した場合、userはnullではないことが保証される
  return user;
}
